import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Parent-portal accounts — list + create surface for the client-
 * detail "Portal Accounts" tab.
 *
 * The PMA server proxies create through admin-server (the identity
 * boundary); list reads PMA's own users table for parents attached
 * to the given client.
 */

export interface ParentAccount {
    id: string;
    first: string;
    last: string;
    email: string;
    status: 'Active' | 'Inactive';
    clientId: string;
    createdAt: string | null;
}

export interface CreateParentInput {
    first_name: string;
    last_name:  string;
    email:      string;
    client_id:  string;
}

/** GET /api/parents?client_id=<uuid> */
export function useClientParents(clientId: string | undefined) {
    return useQuery({
        queryKey: ['client', clientId, 'parents'] as const,
        enabled:  !!clientId,
        queryFn:  async (): Promise<ParentAccount[]> => {
            const res = await api.get<ParentAccount[]>('/parents', {
                params: { client_id: clientId },
            });
            return res.data;
        },
        staleTime: 30_000,
    });
}

/** POST /api/parents — proxies to admin-server's /api/internal/parents. */
export function useCreateParent() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateParentInput): Promise<ParentAccount> => {
            const res = await api.post<ParentAccount>('/parents', input);
            return res.data;
        },
        onSuccess: (created) => {
            qc.invalidateQueries({ queryKey: ['client', created.clientId, 'parents'] });
        },
    });
}
